import type * as api from '@gitbook/api-client';

export interface PageTreeItem {
    id: string;
    page: api.RevisionPage;
    children: PageTreeItem[];
    hiddenByAncestor: boolean;
}

/** Build the nested page tree returned by a complete revision. */
export function createPageTree(revision: api.Revision, spaceId: string): PageTreeItem[] {
    return revision.pages.map((page) => createPageTreeItem(page, spaceId, false));
}

/** Only document pages can be used as a custom site-space homepage. */
export function isSelectablePage(
    page: api.RevisionPage,
    hiddenByAncestor = false
): page is api.RevisionPageDocument {
    return page.type === 'document' && !hiddenByAncestor && !page.hidden;
}

/** Resolve a selectable page target from a complete revision. */
export function resolvePageTarget(
    revision: api.Revision | undefined,
    pageId: string | null | undefined
): api.RevisionPageDocument | null {
    if (!revision || !pageId) {
        return null;
    }

    const find = (
        pages: api.RevisionPage[],
        hiddenByAncestor: boolean
    ): api.RevisionPageDocument | null => {
        for (const page of pages) {
            if (page.id === pageId) {
                return isSelectablePage(page, hiddenByAncestor) ? page : null;
            }
            if ('pages' in page) {
                const result = find(page.pages, hiddenByAncestor || isPageHidden(page));
                if (result) {
                    return result;
                }
            }
        }
        return null;
    };

    return find(revision.pages, false);
}

/** Result of resolving a page target along with the chain of group ancestors above it. */
export interface PageTargetWithAncestors {
    page: api.RevisionPageDocument;
    groups: api.RevisionPage[];
}

/** Resolve a selectable page target from a complete revision, along with its containing groups. */
export function resolvePageTargetWithAncestors(
    revision: api.Revision | undefined,
    pageId: string | null | undefined
): PageTargetWithAncestors | null {
    if (!revision || !pageId) {
        return null;
    }

    const find = (
        pages: api.RevisionPage[],
        groups: api.RevisionPage[],
        hiddenByAncestor: boolean
    ): PageTargetWithAncestors | null => {
        for (const page of pages) {
            if (page.id === pageId) {
                return isSelectablePage(page, hiddenByAncestor) ? { page, groups } : null;
            }
            if ('pages' in page) {
                const result = find(
                    page.pages,
                    [...groups, page],
                    hiddenByAncestor || isPageHidden(page)
                );
                if (result) {
                    return result;
                }
            }
        }
        return null;
    };

    return find(revision.pages, [], false);
}

function createPageTreeItem(
    page: api.RevisionPage,
    spaceId: string,
    hiddenByAncestor: boolean
): PageTreeItem {
    return {
        id: `${spaceId}:${page.id}`,
        page,
        children:
            'pages' in page
                ? page.pages.map((child) =>
                      createPageTreeItem(child, spaceId, hiddenByAncestor || isPageHidden(page))
                  )
                : [],
        hiddenByAncestor,
    };
}

function isPageHidden(page: api.RevisionPage): boolean {
    return 'hidden' in page && page.hidden === true;
}
